"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { fetchMemesQuickVoteDrop, getMemesQuickVoteDropQueryKey } from "@/hooks/memesQuickVote.query";
import { Time } from "@/helpers/time";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
const shouldInvalidateActiveDrop = (drop: ApiDrop, now = Time.currentMillis()): boolean => {
  const profileContext = drop.context_profile_context;
  if (drop.drop_type !== ApiDropType.Participatory) {
    return true;
  }
  if (profileContext?.rating !== 0 || typeof profileContext.max_rating !== "number") {
    return true;
  }

  // Preserve zero-power cards so the queue can show an exhausted state
  // instead of discarding otherwise valid candidates for the session.

  if (!drop.wave.authenticated_user_eligible_to_vote || drop.id.startsWith("temp-")) {
    return true;
  }
  const votingPeriodStart = drop.wave.voting_period_start;
  if (votingPeriodStart !== null && now < votingPeriodStart) {
    return true;
  }
  const votingPeriodEnd = drop.wave.voting_period_end;
  return votingPeriodEnd !== null && now > votingPeriodEnd;
};
export const useMemesQuickVoteActiveDrop = t0 => {
  const $ = _c(33);
  const {
    activeCandidateId,
    discoveredDropsById,
    enabled,
    nextCandidateId,
    onInvalidatedDrop
  } = t0;
  const queryClient = useQueryClient();
  const activeInitialDrop = activeCandidateId ? discoveredDropsById[activeCandidateId] : undefined;
  let t1;
  if ($[0] !== activeCandidateId) {
    t1 = activeCandidateId ? getMemesQuickVoteDropQueryKey(activeCandidateId) : [QueryKey.DROP, {
      context: "memes-quick-vote",
      drop_id: null
    }];
    $[0] = activeCandidateId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activeCandidateId) {
    t2 = () => fetchMemesQuickVoteDrop(activeCandidateId);
    $[2] = activeCandidateId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = enabled && !!activeCandidateId;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getDefaultQueryRetry();
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== activeInitialDrop || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t5 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      initialData: activeInitialDrop,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
      ...t4
    };
    $[5] = activeInitialDrop;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const activeQuery = useQuery(t5);
  const isUsingInitialData = !!activeInitialDrop && activeQuery.data === activeInitialDrop && !activeQuery.isFetchedAfterMount;
  const hasFreshData = !!activeQuery.data && !isUsingInitialData;
  let t6;
  let t7;
  if ($[10] !== enabled || $[11] !== nextCandidateId || $[12] !== queryClient) {
    t6 = () => {
      if (!enabled || !nextCandidateId) {
        return;
      }
      queryClient.prefetchQuery({
        queryKey: getMemesQuickVoteDropQueryKey(nextCandidateId),
        queryFn: () => fetchMemesQuickVoteDrop(nextCandidateId),
        staleTime: 0,
        ...getDefaultQueryRetry()
      });
    };
    t7 = [enabled, nextCandidateId, queryClient];
    $[10] = enabled;
    $[11] = nextCandidateId;
    $[12] = queryClient;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[15] !== activeCandidateId || $[16] !== activeQuery.data || $[17] !== hasFreshData || $[18] !== onInvalidatedDrop) {
    t8 = () => {
      if (!activeCandidateId || !activeQuery.data || !hasFreshData) {
        return;
      }
      if (!shouldInvalidateActiveDrop(activeQuery.data)) {
        return;
      }
      onInvalidatedDrop(activeCandidateId);
    };
    t9 = [activeCandidateId, activeQuery.data, hasFreshData, onInvalidatedDrop];
    $[15] = activeCandidateId;
    $[16] = activeQuery.data;
    $[17] = hasFreshData;
    $[18] = onInvalidatedDrop;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[21] !== activeCandidateId || $[22] !== activeQuery.error || $[23] !== activeQuery.isFetching || $[24] !== onInvalidatedDrop) {
    t10 = () => {
      if (!activeCandidateId || activeQuery.isFetching || !activeQuery.error) {
        return;
      }
      onInvalidatedDrop(activeCandidateId);
    };
    t11 = [activeCandidateId, activeQuery.error, activeQuery.isFetching, onInvalidatedDrop];
    $[21] = activeCandidateId;
    $[22] = activeQuery.error;
    $[23] = activeQuery.isFetching;
    $[24] = onInvalidatedDrop;
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  useEffect(t10, t11);
  let t12;
  bb0: {
    if (!activeQuery.data) {
      t12 = null;
      break bb0;
    }
    let t13;
    if ($[27] !== activeQuery.data) {
      t13 = convertApiDropToExtendedDrop(activeQuery.data);
      $[27] = activeQuery.data;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    t12 = t13;
  }
  const activeDrop = t12;
  const t13 = !!activeCandidateId && !activeQuery.data && (activeQuery.isLoading || activeQuery.isFetching);
  let t14;
  if ($[29] !== activeDrop || $[30] !== hasFreshData || $[31] !== t13) {
    t14 = {
      activeDrop,
      hasFreshData,
      isLoading: t13
    };
    $[29] = activeDrop;
    $[30] = hasFreshData;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
};